import React, { useState, useCallback } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Input, Label, Col, Row, Alert } from 'reactstrap';
import { useApp } from 'src/components/App/appContext';
import graphql from 'src/utils/graphql';
import LoadingButton from 'src/components/LoadingButton';

export default function ProfileSetupModal({ children, open, setOpen, ...args }) {
	const toggle = () => setOpen(!open);
	const [saving, setSaving] = useState(false);
	const { user, fetchUser } = useApp();

	const [state, setState] = useState({
		firstName: user.firstName,
		lastName: user.lastName,
		mobile: user.mobile,
		weight: user.weight,
		height: user.height,
		dob: user.dob,
		gender: user.gender,
		referralSource: user.referralSource
	});

	const [avatarFile, setAvatarFile] = useState(null);

	const isValidValue = (v, key) => {
		if (key === 'referralSource') return true;
		if (key === 'gender') {
			return v === true || v === false;
		}
		return !!v;
	};
	const stateComplete = Object.entries(state).every(([key, value]) => isValidValue(value, key));

	const uploadAvatar = async () => {
		if (!avatarFile) return null;
		
		// Get upload URL from GraphQL
		const {
			objectUploadUrl: { id, uploadURL, url }
		} = await graphql({
			query: `mutation ($input: S3ObjectInput!) {
				objectUploadUrl(input:$input) {
					id
					uploadURL
					url
				}
			}`,
			variables: {
				input: {
					name: avatarFile.name,
					size: avatarFile.size,
					type: avatarFile.type
				}
			}
		});

		// Upload file directly to S3
		const uploadResponse = await fetch(uploadURL, {
			method: 'PUT',
			body: avatarFile,
			headers: {
				'Content-Type': avatarFile.type
			}
		});
		
		if (!uploadResponse.ok) {
			throw new Error('Failed to upload file to S3');
		}
		
		return id; // Return S3 object ID
	};

	const updateProfile = useCallback(async () => {
		try {
			setSaving(true);
			
			// Upload avatar if a new one was selected
			let avatarObjectId = null;
			if (avatarFile) {
				try {
					avatarObjectId = await uploadAvatar();
				} catch (error) {
					console.error('Avatar upload failed:', error);
					// Continue with profile update even if avatar upload fails
				}
			}
			
			const profileData = {
				...state,
				id: user.id
			};
			
			// Add avatar object ID if uploaded
			if (avatarObjectId) {
				profileData.avatar = avatarObjectId;
			}
			
			await graphql({
				query: `mutation ($input: PersonInput!) {
				savePerson(input: $input)
			}`,
				variables: {
					input: profileData
				}
			});
			await fetchUser();
		} catch (error) {
			console.error(error);
		} finally {
			setSaving(false);
		}
	}, [user, state, avatarFile]);

	const handleChange = (e) => {
		const newState = {
			...state,
			[e.target.name]: e.target.value
		};
		if (e.target.name === 'gender') newState.gender = e.target.value === 'true';
		setState(newState);
	};

	const handleAvatarChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			// Validate file type
			if (!file.type.startsWith('image/')) {
				alert('Please select an image file');
				return;
			}
			
			// Validate file size (max 5MB)
			if (file.size > 5 * 1024 * 1024) {
				alert('Please select an image smaller than 5MB');
				return;
			}

			setAvatarFile(file);
		}
	};

	return (
		<>
			<span onClick={toggle}>{children}</span>
			<Modal isOpen={open} toggle={toggle} {...args}>
				<ModalHeader toggle={toggle}>Please complete your profile</ModalHeader>
				<ModalBody>
					{!user.avatarObject?.url && (
						<>
							<Alert color="info" className="mb-3">
								<i className="fa fa-camera"></i> <strong>Optional:</strong> Add a profile photo to help our instructors recognize you on the day of your course!
							</Alert>
							<FormGroup>
								<Label for="avatar">Profile Photo (Optional)</Label>
								<Input 
									type="file" 
									name="avatar"
									id="avatar"
									accept="image/*" 
									onChange={handleAvatarChange}
								/>
								{avatarFile && (
									<small className="text-success">
										<i className="fa fa-check"></i> Photo selected: {avatarFile.name}
									</small>
								)}
							</FormGroup>
							<hr />
						</>
					)}
					
					<Row>
						<Col md={6}>
							<FormGroup floating>
								<Input type="text" name="firstName" id="firstName" value={state.firstName} onChange={handleChange} />
								<Label for="firstName">First Name</Label>
							</FormGroup>
						</Col>
						<Col md={6}>
							<FormGroup floating>
								<Input type="text" name="lastName" id="lastName" value={state.lastName} onChange={handleChange} />
								<Label for="lastName">Last Name</Label>
							</FormGroup>
						</Col>
					</Row>
					<FormGroup floating>
						<Input type="text" name="mobile" id="mobile" value={state.mobile} onChange={handleChange} />
						<Label for="mobile">Mobile</Label>
					</FormGroup>
					<Row>
						<Col md={6}>
							<FormGroup floating>
								<Input type="text" name="weight" id="weight" value={state.weight} onChange={handleChange} />
								<Label for="weight">Weight (KG)</Label>
							</FormGroup>
						</Col>
						<Col md={6}>
							<FormGroup floating>
								<Input type="text" name="height" id="height" value={state.height} onChange={handleChange} />
								<Label for="height">Height (CM)</Label>
							</FormGroup>
						</Col>
						<Col md={6}>
							<FormGroup floating>
								<Input type="date" name="dob" id="dob" value={state.dob} onChange={handleChange} />
								<Label for="dob">Date of Birth</Label>
							</FormGroup>
						</Col>
						<Col md={6}>
							<FormGroup floating>
								<Input type="select" name="gender" id="gender" value={state.gender} onChange={handleChange}>
									<option value={null}>Please Select</option>
									<option value={true}>Male</option>
									<option value={false}>Female</option>
								</Input>
								<Label for="gender">Gender</Label>
							</FormGroup>
						</Col>
					</Row>
					<FormGroup floating>
						<Input
							type="text"
							name="referralSource"
							id="referralSource"
							value={state.referralSource}
							onChange={handleChange}
						/>
						<Label for="referralSource">How did you hear about Green Dragons?</Label>
					</FormGroup>
				</ModalBody>
				<ModalFooter>
					<LoadingButton loading={saving} disabled={!stateComplete} color="primary" onClick={updateProfile}>
						Complete Profile
					</LoadingButton>{' '}
					<Button color="secondary" onClick={toggle}>
						Cancel
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
}
